import { postJangjiRecommendRequest } from "@/apis/sangjoQuotation";
import { isLoggedInState } from "@/atoms/accountState";
import { matchRequestFacilityLoadingState } from "@/atoms/match/matchRequestLoadingState";
import {
  장지유형_LIST,
  장지준비긴급도_LIST,
  장지희망서비스_LIST,
} from "@/containers/MatchContainer/constants/request";
// import { ParlorEstimateCard } from "@/containers/Parlor/ParlorDetailContainer/components/ParlorEstimateCard";
// import { useParlorCompareEstimate } from "@/containers/Parlor/ParlorDetailContainer/hooks/useParlorCompareEstimate";
import { useDialog } from "@/hooks/useDialog";

import { Box, Button, Flex, HStack, VStack } from "@chakra-ui/react";
import {
  call,
  ColorIcon,
  colors,
  cooKeys,
  customEvent,
  Field,
  FUNERAL_SIDO_LIST,
  FUNERAL_SIGUNGU,
  // HackleEventName,
  setCookie,
} from "goi_common";
import { useRouter } from "next/router";
import { Dispatch, useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { FacilityMatchRequestProps } from "../../type/requestType";
import { 장지견적_장지핵심요소_LIST } from "./MatchFacilityRequestQuotation";

export default function RequestQuestionStepByStep({ defaultFacility }: { defaultFacility?: any }) {
  const [currentStep, setCurrentStep] = useState(1);

  const steps = useMemo(() => {
    return [
      {
        step: 1,
      },

      {
        step: 2,
      },

      {
        step: 3,
      },

      {
        step: 4,
      },

      {
        step: 5,
      },
    ];
  }, []);

  const stepTitle = useMemo(() => {
    const titles = [
      defaultFacility?.companyname
        ? "어디에서 장례를 치를 계획이신가요?"
        : "희망하시는 지역은 어디이신가요?",
      "가장 중요하게 생각하시는 부분이 무엇인가요?",
      "장지 준비가 급박한 상황인가요?",
      "희망하시는 추가 서비스가 있으신가요?",
    ];

    return titles[currentStep - 1];
  }, [currentStep]);

  const [selectedOption, setSelectedOption] = useReducer(
    (prev: FacilityMatchRequestProps, next: any) => {
      return { ...prev, ...next };
    },
    {
      장지준비긴급도: "",
      장지유형: "",
      핵심요소: "",
      희망서비스: "",
      funeral_sido: "",
      funeral_sigungu: "",
    },
  );

  const isNextDisabled = useMemo(() => {
    if (currentStep === 1) {
      return !Boolean(selectedOption.funeral_sido && selectedOption.funeral_sigungu);
    } else if (currentStep === 2) {
      return Boolean(!selectedOption.핵심요소);
    } else if (currentStep === 3) {
      return Boolean(!selectedOption.장지준비긴급도);
    } else {
      return Boolean(!selectedOption.희망서비스);
    }
  }, [selectedOption, currentStep]);

  const [regionStep, setRegionStep] = useState(0);
  const [loading, setLoading] = useRecoilState(matchRequestFacilityLoadingState);

  const isLoggedIn = useRecoilValue(isLoggedInState);
  const router = useRouter();

  const eventPath = defaultFacility?.companyname
    ? "match_facility_request"
    : "match_facility_request_crematorium";

  const { onClose } = useDialog();
  const { onOpen: onOpenDialog, onClose: onCloseDialog } = useDialog();

  const handleSubmit = useCallback(async () => {
    if (loading) return;

    customEvent({
      newGtm: {
        path: eventPath,
        trigger_and_target: "submit_conversion",
      },
    });

    try {
      setLoading(true);

      const body = {
        status: 장지준비긴급도_LIST.find((item) => item.text === selectedOption.장지준비긴급도)
          ?.value as string,
        desired_type: 장지유형_LIST.find((item) => item.text === selectedOption.장지유형)
          ?.value as string,
        most_important_factor: selectedOption.핵심요소,
        additional_service: 장지희망서비스_LIST.find(
          (item) => item.text === selectedOption.희망서비스,
        )?.value as string,
        origin_sido: selectedOption.funeral_sido,
        origin_sigungu: selectedOption.funeral_sigungu,
        jangji_sido: defaultFacility?.sido || selectedOption.funeral_sido,
        jangji_sigungu: defaultFacility?.sigungu || selectedOption.funeral_sigungu,
        memo: defaultFacility?.companyname
          ? `[개별 장지 견적 제출]: ${defaultFacility?.companyname}`
          : `[화장장 페이지에서 장지 견적 제출]`,
      };

      const { data, status } = await postJangjiRecommendRequest(body);
      if (status === 201) {
        customEvent({
          newGtm: {
            action: "match_request_complete",
          },
        });

        if (isLoggedIn) {
          router.push(`/match/facility-recommendation/`);
        } else {
          setCookie(cooKeys.quotation_request_facility, data);
          router.push(`/login?redirectTo=/match/facility-recommendation/`).then((res) => {
            setLoading(false);
            onClose();
          });
        }
      }
    } catch (e: any) {
      if (e.response && e.response.status === 500) {
        onOpenDialog({
          type: 1,
          closeOnOverlayClick: false,
          header: "이미 장지 견적을 받았어요",
          children: (
            <>
              작성해주신 견적서를 바탕으로 <br /> 상담이 진행중인 상태에요.
              <br /> 견적 내용을 수정하시려면,
              <br /> 카카오톡 채널로 문의주세요.
            </>
          ),
          footerProps: {
            text: "견적 확인",
            onClick: () => {
              router.push("/match/facility-recommendation/");
              onCloseDialog();
            },
          },
        });
      } else {
        console.log(e);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedOption]);

  const isInitialStepAndSelectedSido = currentStep <= 1 && regionStep <= 1;
  const isInitialStepAndSelectedSidoSigungu = currentStep <= 1 && regionStep <= 2;

  return (
    <VStack width="100%" alignItems="center">
      <VStack maxWidth="480px" width="100%">
        <VStack px="16px">
          <HeaderLayout
            title={
              defaultFacility?.companyname
                ? `${defaultFacility.companyname} 상세 비용 알아보기`
                : "장지 상세 비용 알아보기"
            }
          />
          <StepIndicator steps={steps} currentStep={currentStep} />

          <Box pt="24px" width="100%" whiteSpace="pre-line" className="head2_b black">
            {stepTitle}
          </Box>

          <Box pt="24px" width="100%">
            <StepQuestionB
              eventPath={eventPath}
              currentStep={currentStep}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
              regionStep={regionStep}
              setRegionStep={setRegionStep}
            />
          </Box>

          <HStack
            pt="24px"
            width="100%"
            alignItems="center"
            justifyContent="space-between"
            gap="24px"
          >
            <Box width="100%">
              <PrevNext
                disabled={isInitialStepAndSelectedSido}
                text="이전"
                onClick={() => {
                  if (isInitialStepAndSelectedSidoSigungu) {
                    setRegionStep(1);
                    return;
                  }
                  setCurrentStep(currentStep - 1);
                }}
              />
            </Box>
            <Box width="100%">
              <PrevNext
                disabled={isNextDisabled}
                text={currentStep === 4 ? "비용 확인" : "다음"}
                onClick={() => {
                  currentStep === 4 ? handleSubmit() : setCurrentStep(currentStep + 1);
                }}
              />
            </Box>
          </HStack>

          <Box pt="24px" pb="120px" width="100%">
            <StepQuestionExtraDetail currentStep={currentStep} />
          </Box>
        </VStack>
      </VStack>
    </VStack>
  );
}

const PrevNext = ({
  text,
  disabled,
  onClick,
}: {
  text: string;
  disabled?: boolean;
  onClick: () => void;
}) => {
  return (
    <Button
      onClick={onClick}
      isDisabled={disabled}
      background={colors.orange650}
      color="white"
      _hover={{ background: colors.orange500 }}
      _disabled={{
        background: colors.orange400,
      }}
      borderRadius="36px"
      width="100%"
    >
      {text}
    </Button>
  );
};

const SIDO_LIST = FUNERAL_SIDO_LIST.map((sido) => ({
  key: sido[1] as string,
  value: sido[0],
}));

export interface SelectedOption {
  무의미?: string;
  장례준비긴급도: string;
  sido: null | number;
  sigungu: null | number;
  조문객수: string;
}

const StepQuestionExtraDetail = ({ currentStep }: { currentStep: number }) => {
  // const { estimates } = useParlorCompareEstimate({
  //   isDetail: false,
  //   sido: String(11),
  //   sigungu: "",
  //   isLargeCity: false,
  // });

  if (!currentStep) {
    return null;
  }
  if (currentStep === 1) {
    return (
      <VStack width="100%" borderRadius="6px" background={colors.warmGray100} padding="12px">
        <Box className="subtitle_14_b gray600" width="100%">
          <span className="caption_12_b gray500">
            전체 장례 비용부터 지역별 장지 비용 차이, 상황별 장지 추천까지 한번에 알려드려요
          </span>
        </Box>
      </VStack>
    );
  } else if (currentStep === 2) {
    return (
      <VStack width="100%" borderRadius="6px" background={colors.warmGray100} padding="12px">
        <Box className="subtitle_14_b gray600" width="100%">
          <span className="danger">맞춤형 장지 예상 비용</span>부터{" "}
          <span className="danger">할인혜택</span>까지 한번에 알아볼 수 있어요.
          <br />
          <span className="caption_12_b gray500">
            * 실시간 견적 알아보기를 통해 상세비용, 장지 답사, 할인까지 알아볼 수 있습니다.
          </span>
        </Box>
      </VStack>
    );
  } else if (currentStep === 3) {
    return (
      <VStack width="100%" borderRadius="6px" background={colors.warmGray100} padding="12px">
        <Box width="100%" className="body_14_m gray800" pb="15px">
          장지 비용 뿐만 아니라 전반적인 장례 절차, 임종접수, 긴급 문의가 있으시면 부담없이 아래
          버튼을 통해 연락주세요.
        </Box>
        <HStack width="100%">
          <Button
            flexGrow={1}
            style={{
              borderRadius: 6,
              backgroundColor: colors.gray800,
              padding: "6px 14px",
              boxShadow: "2px 2px 2px #00000022",
            }}
            onClick={() => {
              customEvent({
                newGtm: {
                  path: "match_request",
                  trigger_and_target: "quick_cnslt-opn_urgnt_mdl",
                },
              });
              call("1666-9784");
            }}
          >
            <HStack alignItems="center" justifyContent="center" gap={1}>
              <ColorIcon name="icon-call-mono" color={colors.white} width={12} height={12} />
              <div className="caption_12_sb white">전화 상담</div>{" "}
            </HStack>
          </Button>
          <Button
            flexGrow={1}
            style={{
              borderRadius: 8,
              backgroundColor: colors.gray800,
              padding: "6px 14px",
              boxShadow: "2px 2px 2px #00000022",
            }}
            onClick={() => {
              customEvent({
                newGtm: {
                  path: "match_request",
                  trigger_and_target: "chat",
                },
              });
              window.open("http://pf.kakao.com/_xgFDaK/chat");
            }}
          >
            <HStack alignItems="center" justifyContent="center" gap={1}>
              <ColorIcon name="chat_bubble_rounded" color={colors.white} width={12} height={12} />
              <div className="caption_12_sb white">채팅 상담</div>{" "}
            </HStack>
          </Button>
        </HStack>
      </VStack>
    );
  } else return null;
};

export const StepIndicator = ({ steps, currentStep }: { steps: any[]; currentStep: number }) => {
  return (
    <HStack width="100%" alignItems="center" justifyContent="center" gap={1} paddingRight="0px">
      {steps.map((step, index) => {
        return (
          <Box
            key={step}
            width="8px"
            height="8px"
            borderRadius="50%"
            backgroundColor={currentStep === index + 1 ? colors.gray600 : colors.gray300}
          ></Box>
        );
      })}
    </HStack>
  );
};

export const HeaderLayout = ({ title = "장례 비용 알아보기" }: { title: string }) => {
  return (
    <>
      <VStack gap="10px" padding="24px 0 0" width="100%">
        <HStack alignItems="center" justifyContent="center">
          <Box className="subtitle_16_sb grya900">{title}</Box>
        </HStack>
      </VStack>
    </>
  );
};

const SelectBox = (props: any) => {
  const children = props.children;
  const isSelected = props.isSelected;
  return (
    <Box
      {...props}
      width="33%"
      height="48px"
      border={`0.5px solid ${colors.gray300}`}
      textAlign="center"
      className="body_16_m gray700"
      lineHeight="48px"
      background={isSelected ? colors.black : ""}
      color={isSelected ? colors.white : ""}
      _hover={{
        background: isSelected ? "" : colors.warmGray200,
        cursor: "pointer",
      }}
    >
      {children}
    </Box>
  );
};

export const StepQuestionB = ({
  eventPath,
  currentStep,
  selectedOption,
  setSelectedOption,
  regionStep,
  setRegionStep,
}: {
  eventPath: "match_facility_request" | "match_facility_request_crematorium";
  currentStep: number;
  selectedOption: FacilityMatchRequestProps;
  setSelectedOption: Dispatch<Partial<FacilityMatchRequestProps>>;
  regionStep: number;
  setRegionStep: Dispatch<number>;
}) => {
  const sigunguValues = useMemo(() => {
    if (!selectedOption.funeral_sido) {
      return [];
    }

    const currentSigunguList = Array.from(FUNERAL_SIGUNGU.get(selectedOption.funeral_sido)!);

    return currentSigunguList.map((sigungu) => {
      const [code, name] = sigungu;

      return {
        key: name,
        value: code,
      };
    });
  }, [selectedOption.funeral_sido]);

  useEffect(() => {
    if (currentStep === 1) {
      setRegionStep(1);
    }
  }, [currentStep]);

  if (currentStep === 1) {
    return (
      <VStack width="100%" alignItems="start" pb="64px">
        {regionStep === 1 && (
          <Flex flexWrap="wrap" width="100%">
            {SIDO_LIST.map((sido) => {
              return (
                <SelectBox
                  key={sido.key}
                  onClick={() => {
                    const v = sido.value;
                    setSelectedOption({ funeral_sido: v, funeral_sigungu: undefined });
                    customEvent({
                      newGtm: {
                        path: eventPath,
                        trigger_and_target: "facility_funeral_sido-answer",
                      },
                    });
                    setRegionStep(2);
                  }}
                  isSelected={sido.value === selectedOption.funeral_sido}
                >
                  {sido.key}
                </SelectBox>
              );
            })}
          </Flex>
        )}
        {regionStep === 2 && (
          <Flex flexWrap="wrap" width="100%">
            {sigunguValues.map((sigungu) => {
              return (
                <SelectBox
                  key={sigungu.key}
                  onClick={() => {
                    const v = sigungu.value;

                    setSelectedOption({ funeral_sigungu: v });
                    customEvent({
                      newGtm: {
                        path: eventPath,
                        trigger_and_target: "facility_funeral_sigungu-answer",
                      },
                    });
                  }}
                  isSelected={sigungu.value === selectedOption.funeral_sigungu}
                >
                  {sigungu.key}
                </SelectBox>
              );
            })}
          </Flex>
        )}
      </VStack>
    );
  } else if (currentStep === 2) {
    return (
      <VStack width="100%">
        <Field.ButtonGroups
          items={장지견적_장지핵심요소_LIST}
          value={selectedOption.핵심요소}
          onChange={(e) => {
            setSelectedOption({ 핵심요소: e });
            customEvent({
              newGtm: {
                path: eventPath,
                trigger_and_target: "jangji_important_factor-answer",
                text: String(e),
              },
            });
          }}
          borderColor={colors.white}
          buttonWidth="100%"
        />
      </VStack>
    );
  } else if (currentStep === 3) {
    return (
      <VStack width="100%">
        <Field.ButtonGroups
          items={장지준비긴급도_LIST.map((item) => item.text)}
          value={selectedOption.장지준비긴급도}
          onChange={(e) => {
            setSelectedOption({ 장지준비긴급도: e });
            customEvent({
              newGtm: {
                path: eventPath,
                trigger_and_target: "jangji_status-answer",
              },
            });
          }}
          buttonWidth="100%"
          borderColor="white"
        />
      </VStack>
    );
  } else if (currentStep === 4) {
    return (
      <VStack width="100%">
        <Field.ButtonGroups
          items={장지희망서비스_LIST.map((item) => item.text)}
          value={selectedOption.희망서비스}
          onChange={(e) => {
            setSelectedOption({ 희망서비스: e });
            customEvent({
              newGtm: {
                path: eventPath,
                trigger_and_target: "jangji_desired_service-answer",
                text: String(e),
              },
            });
          }}
          buttonWidth="100%"
          borderColor="white"
        />
      </VStack>
    );
  } else return null;
};
